import { useEffect, useMemo, useRef } from 'react';
import { ArrowBackOutlined, DescriptionOutlined, EditOutlined } from '@mui/icons-material';
import { Alert, Box, Button, Divider, Stack, Typography } from '@mui/material';
import {
  DefinitionList,
  InformationPage,
  PageSections,
  ProgressStep,
  SectionCard,
  ValueBadge,
} from '../../../design-system';
import type { WorkflowTemplate } from '../model';
import type { WorkflowOrganizationOption, WorkflowPersonOption } from '../WorkflowReferencePickers';
import { workflowFieldTypeLabel } from '../WorkflowFieldsEditor';
import { groupWorkflowApprovalSteps } from '../workflowApprovalStages';
import { accessRuleLabel, approvalDescription, workflowTemplateToDraft } from '../models/workflowTemplatePageModel';
import { Status } from '../components/WorkflowStatus';
import { WorkflowTemplateLayout } from '../components/WorkflowTemplateLayout';
import { WorkflowTemplatePreview } from '../components/WorkflowTemplatePreview';

type WorkflowTemplateDetailPageProps = {
  template: WorkflowTemplate;
  canWrite: boolean;
  personOptions: readonly WorkflowPersonOption[];
  organizationOptions: readonly WorkflowOrganizationOption[];
  personOptionsLoading: boolean;
  personOptionsError?: string;
  onRetryPersonOptions: () => void;
  notice: string;
  onDismissNotice: () => void;
  onBack: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onArchive: () => void;
};

export function WorkflowTemplateDetailPage({
  template,
  canWrite,
  personOptions,
  organizationOptions,
  personOptionsLoading,
  personOptionsError,
  onRetryPersonOptions,
  notice,
  onDismissNotice,
  onBack,
  onEdit,
  onDuplicate,
  onArchive,
}: WorkflowTemplateDetailPageProps) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const draft = useMemo(() => workflowTemplateToDraft(template), [template]);
  const approvalStages = groupWorkflowApprovalSteps(template.version.approvalSteps);
  useEffect(() => { titleRef.current?.focus(); }, [template.id]);

  return <InformationPage
    title={template.displayName}
    titleRef={titleRef}
    width="wide"
    testId="workflow-template-detail"
    navigation={<Button startIcon={<ArrowBackOutlined />} onClick={onBack}>양식 목록</Button>}
    primaryAction={canWrite ? <Button variant="fill" startIcon={<EditOutlined />} onClick={onEdit}>{template.version.status === 'DRAFT' ? '초안 편집' : '새 버전 만들기'}</Button> : undefined}
  >
    <PageSections>
      {notice && <Alert severity="info" onClose={onDismissNotice}>{notice}</Alert>}
      <WorkflowTemplateLayout preview={<WorkflowTemplatePreview
        value={draft}
        personOptions={personOptions}
        organizationOptions={organizationOptions}
        loading={personOptionsLoading}
        error={personOptionsError}
        onRetry={onRetryPersonOptions}
      />}>
        <SectionCard title="기본 정보" bodyPadding="standard" action={canWrite ? <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<DescriptionOutlined />} onClick={onDuplicate}>양식 복제</Button>
          <Button color="error" onClick={onArchive}>양식 보관</Button>
        </Stack> : undefined}>
          <DefinitionList items={[
            ['참조 코드', template.code],
            ['분류', template.category ? <ValueBadge label={template.category} /> : '-'],
            ['설명', template.version.description || '-'],
            ['양식 상태', <Status value={template.status} />],
            ['버전', `v${template.version.version}`],
            ['버전 상태', <Status value={template.version.status} />],
            ['구성', `필드 ${template.version.fields.length}개 · 결재 ${approvalStages.length}단계`],
          ]} />
        </SectionCard>
        <SectionCard title="양식 필드">
          <Stack component="ol" aria-label="양식 필드" divider={<Divider component="li" role="presentation" />} sx={{ m: 0, p: 0, listStyle: 'none' }}>
            {template.version.fields.map((field) => <Box component="li" key={field.key} sx={{ p: 2, minWidth: 0 }}>
              <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{field.label}</Typography>
                <ValueBadge size="compact" label={workflowFieldTypeLabel(field.type)} />
                {field.required && <ValueBadge size="compact" label="필수" />}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, overflowWrap: 'anywhere' }}>{field.key}</Typography>
              {field.helpText && <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{field.helpText}</Typography>}
            </Box>)}
          </Stack>
        </SectionCard>
        <SectionCard title="사용 권한">
          <Stack divider={<Divider />}>{template.version.accessRules.map((rule, index) => <Stack key={`${rule.capability}-${rule.principalType}-${index}`} direction="row" spacing={1} useFlexGap sx={{ p: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <ValueBadge label={accessRuleLabel(rule)} />
            {rule.principalType === 'ROLE' && rule.principalId ? <ValueBadge label={rule.principalId} /> : <Typography variant="body2" color="text.secondary">{rule.principalId || '전체 구성원'}</Typography>}
          </Stack>)}</Stack>
        </SectionCard>
        <SectionCard title="기본 결재선" bodyPadding="standard">
          <Stack spacing={2} divider={<Divider />}>{approvalStages.map((stage) => <Stack key={stage.stageOrder} spacing={1}>
            <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="subtitle2">{stage.stageOrder}단계</Typography>
              {stage.items.length > 1 && <ValueBadge label={`병렬 승인 · ${stage.items.length}명`} />}
            </Stack>
            {stage.items.map(({ step }) => <ProgressStep
              key={step.order}
              marker={stage.stageOrder}
              label={step.label}
              description={`${approvalDescription(step)} · ${step.requesterEditable ? '요청자가 수정 가능' : '고정 단계'}`}
            />)}
          </Stack>)}</Stack>
        </SectionCard>
      </WorkflowTemplateLayout>
    </PageSections>
  </InformationPage>;
}
